import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import { RocketIcon, PaletteIcon, BrainIcon, VideoIcon, TrendingUpIcon, CheckCircleIcon, SparklesIcon, ArrowRightIcon } from 'lucide-react';
const Services = () => {
  const services = [{
    id: 'prototipos',
    title: 'Desarrollo de Prototipos',
    description: 'Convertimos conceptos en prototipos funcionales que demuestran la viabilidad de tu idea y permiten realizar pruebas con usuarios reales.',
    icon: <RocketIcon className="h-12 w-12" data-id="element-514" />,
    image: 'https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
    gradient: 'from-purple-600/20 to-pink-600/20',
    features: ['Prototipado rápido de interfaces', 'Desarrollo de MVPs', 'Pruebas de usabilidad', 'Iteración basada en feedback']
  }, {
    id: 'diseno',
    title: 'Diseño de Proyectos',
    description: 'Creamos experiencias visuales memorables que conectan con tu audiencia y refuerzan la identidad de tu marca.',
    icon: <PaletteIcon className="h-12 w-12" data-id="element-515" />,
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
    gradient: 'from-pink-600/20 to-purple-600/20',
    features: ['Diseño UI/UX', 'Identidad visual', 'Diseño de interacción', 'Sistemas de diseño']
  }, {
    id: 'ia',
    title: 'Inteligencia Artificial',
    description: 'Implementamos tecnologías de inteligencia artificial para optimizar procesos y crear experiencias innovadoras.',
    icon: <BrainIcon className="h-12 w-12" data-id="element-516" />,
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
    gradient: 'from-blue-600/20 to-purple-600/20',
    features: ['Chatbots y asistentes virtuales', 'Análisis predictivo', 'Automatización de procesos', 'Personalización basada en IA']
  }, {
    id: 'audiovisual',
    title: 'Producción Audiovisual',
    description: 'Producimos contenido audiovisual de alta calidad que cuenta historias impactantes y conecta con tu audiencia.',
    icon: <VideoIcon className="h-12 w-12" data-id="element-517" />,
    image: 'https://images.unsplash.com/photo-1574717024453-e599f3984a15?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
    gradient: 'from-pink-600/20 to-red-600/20',
    features: ['Producción de videos corporativos', 'Spots publicitarios', 'Contenido para redes sociales', 'Animación y motion graphics']
  }, {
    id: 'consultoria',
    title: 'Marketing Digital',
    description: 'Asesoramos estratégicamente para potenciar tu presencia en el mercado y alcanzar tus objetivos de negocio.',
    icon: <TrendingUpIcon className="h-12 w-12" data-id="element-518" />,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
    gradient: 'from-green-600/20 to-blue-600/20',
    features: ['Estrategia de marketing digital', 'Posicionamiento de marca', 'Análisis de mercado', 'Optimización de conversión']
  }];
  return <>
      {/* Hero Section - Full Screen */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-id="element-519">
        <div className="absolute inset-0" data-id="element-520">
          <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Services" className="w-full h-full object-cover" data-id="element-521" />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-purple-900/85 to-gray-900/90" data-id="element-522"></div>
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1
      }} className="container mx-auto px-4 md:px-6 relative z-10 text-center" data-id="element-523">
          <div className="flex items-center justify-center mb-6" data-id="element-524">
            <SparklesIcon className="h-12 w-12 text-purple-400 mr-4" data-id="element-525" />
            <h1 className="text-5xl md:text-7xl font-bold text-white" data-id="element-526">
              Nuestros Servicios
            </h1>
          </div>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto" data-id="element-527">
            Soluciones diseñadas a medida que reflejan nuestra pasión por la{' '}
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent font-semibold" data-id="element-528">
              creatividad y la excelencia
            </span>
          </p>
        </motion.div>
        {/* Scroll Indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2" data-id="element-529">
          <motion.div animate={{
          y: [0, 10, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} className="text-white" data-id="element-530">
            <ArrowRightIcon className="h-8 w-8 rotate-90" data-id="element-531" />
          </motion.div>
        </motion.div>
      </section>
      {/* Services Grid */}
      <section className="py-32 bg-gray-900 relative overflow-hidden" data-id="element-532">
        <div className="absolute inset-0 opacity-10" data-id="element-533">
          <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl" data-id="element-534"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-500 rounded-full blur-3xl" data-id="element-535"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-536">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} className="text-center mb-20" data-id="element-537">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" data-id="element-538">
              Transformamos{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-id="element-539">
                ideas en realidad
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-540">
              Cada servicio está diseñado para impulsar tu visión al siguiente
              nivel
            </p>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" data-id="element-541">
            {services.slice(0, 4).map((service, index) => <motion.div key={service.id} initial={{
            opacity: 0,
            y: 50
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: index * 0.1
          }} viewport={{
            once: true
          }} className="group relative h-[500px] rounded-2xl overflow-hidden cursor-pointer" data-id="element-542">
                <div className="absolute inset-0" data-id="element-543">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-544" />
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} group-hover:opacity-90 transition-opacity duration-500`} data-id="element-545"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent" data-id="element-546"></div>
                </div>
                <div className="relative h-full p-8 flex flex-col justify-end" data-id="element-547">
                  <div className="text-purple-400 mb-4 transform group-hover:scale-110 transition-transform duration-500" data-id="element-548">
                    {service.icon}
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-3" data-id="element-549">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 mb-6" data-id="element-550">{service.description}</p>
                  <div className="space-y-2 mb-6" data-id="element-551">
                    {service.features.map((feature, i) => <div key={i} className="flex items-center text-gray-300" data-id="element-552">
                        <CheckCircleIcon className="h-4 w-4 text-purple-400 mr-2 flex-shrink-0" data-id="element-553" />
                        <span className="text-sm" data-id="element-554">{feature}</span>
                      </div>)}
                  </div>
                  <Link to="/contacto" className="flex items-center text-purple-400 font-semibold hover:text-purple-300 transition-colors" data-id="element-555">
                    <span data-id="element-556">Más información</span>
                    <ArrowRightIcon className="ml-2 h-4 w-4 transform group-hover:translate-x-2 transition-transform duration-300" data-id="element-557" />
                  </Link>
                </div>
              </motion.div>)}
          </div>
          {/* Last service - Full width */}
          {services.slice(4).map((service, index) => <motion.div key={service.id} initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} className="group relative h-[500px] rounded-2xl overflow-hidden cursor-pointer" data-id="element-558">
              <div className="absolute inset-0" data-id="element-559">
                <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-560" />
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} group-hover:opacity-90 transition-opacity duration-500`} data-id="element-561"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent" data-id="element-562"></div>
              </div>
              <div className="relative h-full p-8 flex flex-col justify-end max-w-2xl" data-id="element-563">
                <div className="text-purple-400 mb-4 transform group-hover:scale-110 transition-transform duration-500" data-id="element-564">
                  {service.icon}
                </div>
                <h3 className="text-3xl font-bold text-white mb-3" data-id="element-565">
                  {service.title}
                </h3>
                <p className="text-gray-300 mb-6" data-id="element-566">{service.description}</p>
                <div className="space-y-2 mb-6" data-id="element-567">
                  {service.features.map((feature, i) => <div key={i} className="flex items-center text-gray-300" data-id="element-568">
                      <CheckCircleIcon className="h-4 w-4 text-purple-400 mr-2 flex-shrink-0" data-id="element-569" />
                      <span className="text-sm" data-id="element-570">{feature}</span>
                    </div>)}
                </div>
                <Link to="/contacto" className="flex items-center text-purple-400 font-semibold hover:text-purple-300 transition-colors" data-id="element-571">
                  <span data-id="element-572">Más información</span>
                  <ArrowRightIcon className="ml-2 h-4 w-4 transform group-hover:translate-x-2 transition-transform duration-300" data-id="element-573" />
                </Link>
              </div>
            </motion.div>)}
        </div>
      </section>
      {/* Process Section */}
      <section className="relative py-32 flex items-center" data-id="element-574">
        <div className="absolute inset-0" data-id="element-575">
          <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Process" className="w-full h-full object-cover" data-id="element-576" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-purple-900/80 to-gray-900/95" data-id="element-577"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-578">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} viewport={{
          once: true
        }} className="max-w-3xl" data-id="element-579">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-580">
              Nuestro{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-id="element-581">
                proceso
              </span>
            </h2>
            <div className="space-y-8" data-id="element-582">
              {[{
              step: '01',
              title: 'Consulta inicial',
              description: 'Discutimos tus necesidades, objetivos y visión del proyecto'
            }, {
              step: '02',
              title: 'Propuesta detallada',
              description: 'Desarrollamos una estrategia completa con cronograma y presupuesto'
            }, {
              step: '03',
              title: 'Desarrollo e iteración',
              description: 'Creamos tu proyecto con feedback constante y ajustes en tiempo real'
            }, {
              step: '04',
              title: 'Entrega y soporte',
              description: 'Lanzamos tu proyecto y ofrecemos soporte continuo'
            }].map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.8,
              delay: index * 0.1
            }} viewport={{
              once: true
            }} className="flex items-start" data-id="element-583">
                  <div className="text-5xl font-bold text-purple-400/30 mr-6" data-id="element-584">
                    {item.step}
                  </div>
                  <div data-id="element-585">
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-586">
                      {item.title}
                    </h3>
                    <p className="text-gray-300" data-id="element-587">{item.description}</p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>
      {/* CTA Section - Full Screen */}
      <section className="relative h-screen flex items-center justify-center" data-id="element-588">
        <div className="absolute inset-0" data-id="element-589">
          <img src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Contact" className="w-full h-full object-cover" data-id="element-590" />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-purple-900/90 to-gray-900/80" data-id="element-591"></div>
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 50
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1
      }} viewport={{
        once: true
      }} className="container mx-auto px-4 md:px-6 text-center relative z-10" data-id="element-592">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white" data-id="element-593">
            ¿Listo para comenzar
            <br data-id="element-594" />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-id="element-595">
              tu próximo proyecto?
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto" data-id="element-596">
            Estamos aquí para convertir tu visión en realidad
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-597">
            <Button href="/contacto" variant="primary" size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg px-8 py-4" data-id="element-598">
              Solicitar presupuesto
              <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-599" />
            </Button>
            <Button href="/proyectos" variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-4" data-id="element-600">
              Ver proyectos
            </Button>
          </div>
        </motion.div>
      </section>
    </>;
};
export default Services;